import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal } from '@/components_deprecated/Modal'
import { Form } from '@/components_deprecated/Form'
import styled from 'styled-components'
import {
  differenceInMinutes,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  parseISO,
} from 'date-fns'
import type { ApproveWorkPeriodActivityNode } from './types.graphql'
import { Checkbox, DatePicker, TimePicker } from '@/components'
import { isMobile } from 'react-device-detect'
import { useActivityColor } from '@/utils'

interface FieldProps {
  alignLeft?: boolean
  alignRight?: boolean
  area?: string
}
export const Field = styled.div<FieldProps>`
  grid-area: ${props => props.area};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${props => props.alignLeft && 'align-items: flex-start;'}
  ${props => props.alignRight && 'align-items: flex-end;'}

  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    color: ${props => props.theme.colors.gray5};
  }
`

const StyledForm = styled(Form)`
  gap: 0.5rem;
`

const DateTimeField = styled(Field)`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 0.5rem;

  label {
    grid-row: 1;
    grid-column: 1 / 3;
    margin: 0 0 0 0;
  }
  ${props => props.theme.media.mobile} {
    .--time-picker-inner-wrapper {
      width: 80px;
      padding: 0;
    }
  }
`

export interface ExcludeActivityPromptData {
  excludeFromSalaryCalculation: boolean
  excludeBefore: Date | null
  excludeAfter: Date | null
}

interface ExcludeActivityPromptProps {
  activity: ApproveWorkPeriodActivityNode
  onSubmit: (data: ExcludeActivityPromptData | null) => void
}

export const ExcludeActivityPrompt: React.FC<ExcludeActivityPromptProps> = ({
  activity,
  onSubmit,
}) => {
  const activityStart = parseISO(activity.datetimeStart)
  const activityEnd = parseISO(activity.datetimeEnd)
  const activityIsSameDay = isSameDay(activityStart, activityEnd)
  const initialExcludeBeforeString =
    activity.excludeFromSalaryCalculation && activity.excludeBefore
      ? activity.excludeBefore
      : activity.datetimeStart
  const initialExcludeAfterString =
    activity.excludeFromSalaryCalculation && activity.excludeAfter
      ? activity.excludeAfter
      : activity.datetimeEnd
  const initialExcludeBefore = parseISO(initialExcludeBeforeString)
  const initialExcludeAfter = parseISO(initialExcludeAfterString)
  const { formValues, updateForm } = useForm<ExcludeActivityPromptData>({
    values: {
      excludeFromSalaryCalculation: activity.excludeFromSalaryCalculation,
      excludeBefore: activity.excludeBefore ? initialExcludeBefore : null,
      excludeAfter: activity.excludeAfter ? initialExcludeAfter : null,
    },
    validators: {
      excludeBefore: (value, others) => {
        if (!value) return null
        if (
          isAfter(value, activityStart) &&
          (!others.excludeAfter || isBefore(value, others.excludeAfter))
        ) {
          return 'timesheets.error.start-before-end'
        }
        return null
      },
      excludeAfter: (value, others) => {
        if (!value) return null
        if (
          isBefore(value, activityEnd) &&
          (!others.excludeBefore || isAfter(value, others.excludeBefore))
        ) {
          return 'timesheets.error.start-before-end'
        }
        return null
      },
    },
  })

  const getActivityColor = useActivityColor()
  const translations = useTranslate({
    label: {
      excludeFromSalaryCalculation: 'gantt.remove-from-salary-calculation',
      excludeBefore: 'common.exclude-before',
      excludeAfter: 'common.exclude-after',
      salaryCode: 'common.salary-code',
      amount: 'common.amount',
      comment: 'common.comment',
    },
    create: 'common.create-alt',
    update: 'common.update',

    error: {
      startBeforeEnd: 'timesheets.error.start-before-end',
      required: 'common.required',
    },
  })

  function handleSubmit() {
    if (!formValues.excludeFromSalaryCalculation) {
      onSubmit({
        excludeFromSalaryCalculation: false,
        excludeBefore: null,
        excludeAfter: null,
      })
      return
    }
    let returnBefore = formValues.excludeBefore
    let returnAfter = formValues.excludeAfter
    if (formValues.excludeBefore && formValues.excludeAfter) {
      if (isEqual(formValues.excludeBefore, activityStart)) {
        returnBefore = null
      }
      if (isEqual(formValues.excludeAfter, activityEnd)) {
        returnAfter = null
      }
    }
    onSubmit({
      ...formValues,
      excludeBefore: returnBefore,
      excludeAfter: returnAfter,
    })
  }

  const excludeBeforeComparisonValue = formValues.excludeBefore ?? activityStart
  const excludeAfterComparisonValue = formValues.excludeAfter ?? activityEnd
  const totalActivityMinutes = differenceInMinutes(activityEnd, activityStart)

  const excludeBeforePercentage = !formValues.excludeBefore
    ? 0
    : differenceInMinutes(excludeBeforeComparisonValue, activityStart) /
      totalActivityMinutes

  const excludeAfterPercentage = !formValues.excludeAfter
    ? 0
    : differenceInMinutes(activityEnd, excludeAfterComparisonValue) /
      totalActivityMinutes

  return (
    <RegularModal
      title={translations.label.excludeFromSalaryCalculation}
      submitText={translations.update}
      width={isMobile ? '90%' : '600px'}
      onCancel={() => onSubmit(null)}
      onSubmit={() => handleSubmit()}
    >
      <StyledForm onSubmit={() => handleSubmit()}>
        <Field>
          <label>{translations.label.excludeFromSalaryCalculation}</label>
          <Checkbox
            value={formValues.excludeFromSalaryCalculation}
            onChange={value =>
              updateForm({ excludeFromSalaryCalculation: !!value })
            }
          />
        </Field>
        {formValues.excludeFromSalaryCalculation && (
          <>
            <DateTimeField>
              <label>{translations.label.excludeBefore}</label>
              <TimePicker
                value={formValues.excludeBefore ?? initialExcludeBefore}
                onChange={value => {
                  if (!value) return
                  updateForm({
                    excludeBefore: isBefore(value, activityStart)
                      ? activityStart
                      : isAfter(value, formValues.excludeAfter ?? activityEnd)
                      ? formValues.excludeAfter ?? activityEnd
                      : value,
                  })
                }}
              />
              {!activityIsSameDay && (
                <DatePicker
                  mode="single"
                  clearable={false}
                  value={formValues.excludeBefore}
                  onChange={value => {
                    const newExcludeBefore = value
                    if (!newExcludeBefore) return
                    newExcludeBefore.setHours(initialExcludeBefore.getHours())
                    newExcludeBefore.setMinutes(
                      initialExcludeBefore.getMinutes()
                    )
                    updateForm({
                      excludeBefore: isBefore(newExcludeBefore, activityEnd)
                        ? newExcludeBefore
                        : activityEnd,
                    })
                  }}
                />
              )}
            </DateTimeField>
            <DateTimeField>
              <label>{translations.label.excludeAfter}</label>

              <TimePicker
                value={formValues.excludeAfter ?? initialExcludeAfter}
                onChange={value => {
                  const newExcludeAfter = value
                  if (!newExcludeAfter) return
                  updateForm({
                    excludeAfter: isAfter(newExcludeAfter, activityEnd)
                      ? activityEnd
                      : isBefore(
                          newExcludeAfter,
                          formValues.excludeBefore ?? activityStart
                        )
                      ? formValues.excludeBefore ?? activityEnd
                      : newExcludeAfter,
                  })
                }}
              />
              {!activityIsSameDay && (
                <DatePicker
                  mode="single"
                  clearable={false}
                  value={formValues.excludeAfter}
                  onChange={value => {
                    const newExcludeAfter = value
                    if (!newExcludeAfter) return
                    newExcludeAfter.setHours(initialExcludeAfter.getHours())
                    newExcludeAfter.setMinutes(initialExcludeAfter.getMinutes())
                    updateForm({
                      excludeAfter: isAfter(
                        newExcludeAfter,
                        initialExcludeAfter
                      )
                        ? initialExcludeAfter
                        : newExcludeAfter,
                    })
                  }}
                />
              )}
            </DateTimeField>
            <div className="mb-2 mt-2 flex h-6 w-full overflow-hidden rounded">
              <div
                style={{
                  width: `${excludeBeforePercentage * 100}%`,
                  backgroundColor: getActivityColor({
                    ...activity,
                    excludeFromSalaryCalculation: true,
                    excludeAfter: null,
                    excludeBefore: null,
                  }),
                  borderRight: '1px solid white',
                }}
              ></div>
              <div
                style={{
                  width: `${
                    (1 - excludeAfterPercentage - excludeBeforePercentage) * 100
                  }%`,
                  backgroundColor: getActivityColor({
                    ...activity,
                    excludeFromSalaryCalculation:
                      (!formValues.excludeBefore ||
                        isEqual(formValues.excludeBefore, activityStart)) &&
                      (!formValues.excludeAfter ||
                        isEqual(formValues.excludeAfter, activityEnd)),
                    excludeAfter: null,
                    excludeBefore: null,
                  }),
                }}
              ></div>
              <div
                style={{
                  width: `${excludeAfterPercentage * 100}%`,
                  backgroundColor: getActivityColor({
                    ...activity,
                    excludeFromSalaryCalculation: true,
                    excludeAfter: null,
                    excludeBefore: null,
                  }),
                  borderLeft: '1px solid white',
                }}
              ></div>
            </div>
          </>
        )}
      </StyledForm>
    </RegularModal>
  )
}
